// J-243 (the owner's order of 2026-10-16, item 2): the page a sign-in returns to belongs to one user.
// Lock session, the idle sign-out, a 401 and the route guard all send the user to the sign-in page with
// the page they were on, so that the same person signing back in returns to it. Before this, whoever
// signed in next was sent there: a group user signing in after the admin opened the admin's page and
// filters and met a 403. Now the tab remembers which user was signed in last, by the opaque user id
// (never the email), and the page to return to is kept only for that user; anyone else lands on their
// own default page.
//
// The id lives in sessionStorage, not only in memory, because the sign-in page is also reached by a
// full load (a reload, Back after Log out, a restored tab), which empties memory and would make the
// previous user's page look like an anonymous visitor's link. It is tab-scoped, gone when the tab
// closes, and is not PHI (`.claude/rules/phi.md` rule 3). It is kept past sign-out on purpose: it is
// what tells the next sign-in that the page in the URL is someone else's.
import { safeRedirect } from "./session-read";

/** The sessionStorage key holding the opaque id of the user last signed in in this tab. */
export const LAST_USER_KEY = "vbltc.last-user";

/** The part of `Storage` this file uses. */
export interface KeyStore {
  getItem: (key: string) => string | null;
  setItem: (key: string, value: string) => void;
}

/** Used when the browser refuses sessionStorage (a private window's quota, a blocked origin). */
const memory = new Map<string, string>();
const MEMORY_STORE: KeyStore = {
  getItem: (key) => memory.get(key) ?? null,
  setItem: (key, value) => void memory.set(key, value),
};

/** This tab's sessionStorage, or the in-memory stand-in when it cannot be used. */
function tabStore(): KeyStore {
  try {
    const store = globalThis.sessionStorage as KeyStore | undefined;
    if (store === undefined) return MEMORY_STORE;
    store.getItem(LAST_USER_KEY); // throws where storage is blocked
    return store;
  } catch {
    return MEMORY_STORE;
  }
}

/** The user last signed in in this tab, or undefined when nobody has been. */
export function lastSignedInUser(store: KeyStore = tabStore()): string | undefined {
  try {
    return store.getItem(LAST_USER_KEY) ?? undefined;
  } catch {
    return MEMORY_STORE.getItem(LAST_USER_KEY) ?? undefined;
  }
}

/** Records the signed-in user's opaque id (the route guard, on every session it reads). */
export function rememberSignedInUser(userId: string, store: KeyStore = tabStore()): void {
  try {
    store.setItem(LAST_USER_KEY, userId);
  } catch {
    MEMORY_STORE.setItem(LAST_USER_KEY, userId);
  }
}

/**
 * Where a sign-in by `userId` goes: the page to return to when this tab's last user is the same user
 * or nobody (a link opened before signing in), else "/" — the user's own default page. An answer with
 * no user id counts as a different user, so an unreadable answer never opens another user's page.
 */
export function returnTargetFor(
  redirect: string | undefined,
  userId: string | undefined,
  previous: string | undefined,
): string {
  if (previous !== undefined && previous !== userId) return "/";
  return safeRedirect(redirect);
}

/** `returnTargetFor` with this tab's last user, which then becomes `userId`. */
export function returnTarget(
  redirect: string | undefined,
  userId: string | undefined,
  store: KeyStore = tabStore(),
): string {
  const target = returnTargetFor(redirect, userId, lastSignedInUser(store));
  if (userId !== undefined) rememberSignedInUser(userId, store);
  return target;
}

/** The user id in a sign-in or two-factor answer (Better Auth sends `{ user: { id } }`). */
export function signedInUserId(data: unknown): string | undefined {
  if (typeof data !== "object" || data === null) return undefined;
  const id = (data as { user?: { id?: unknown } }).user?.id;
  return typeof id === "string" && id !== "" ? id : undefined;
}
